import { ERRORS } from '@/common/constants/errors';
import { PLAN, TPlan } from '@/common/constants/markets';
import { useGetSavingsTotal } from '@/common/integrations/menhir/employee/savings/savings.hooks';

export type TPersonalInfoSection =
  | 'personal'
  | 'address'
  | 'contact'
  | 'bank'
  | 'taxProfile'
  | 'documents';

export type TPersonalDetailsField =
  | 'nationality'
  | 'birthdate'
  | 'gender'
  | 'birthplace'
  | 'fiscalRegion';

const sectionsByPlan: Record<TPlan, TPersonalInfoSection[]> = {
  [PLAN.SUC_MEAL]: ['personal', 'address', 'contact'],
  [PLAN.WALLET]: [
    'personal',
    'address',
    'contact',
    'bank',
    'taxProfile',
    'documents',
  ],
  [PLAN.ITALY]: ['personal'],
  [PLAN.ITALY_FULL]: ['personal', 'contact', 'bank'],
  [PLAN.ITALY_FRINGE]: ['personal', 'contact', 'bank'],
  [PLAN.ES_MEAL]: ['personal', 'address', 'contact'],
  [PLAN.ES_WALLET]: ['personal', 'address', 'contact'],
  [PLAN.ES_HYBRID]: ['personal', 'address', 'contact'],
};

const personalFieldsByPlan: Record<TPlan, TPersonalDetailsField[]> = {
  [PLAN.SUC_MEAL]: ['nationality', 'birthdate'],
  [PLAN.WALLET]: ['nationality', 'birthdate', 'gender', 'birthplace'],
  [PLAN.ITALY]: [],
  [PLAN.ITALY_FULL]: [],
  [PLAN.ITALY_FRINGE]: [],
  [PLAN.ES_MEAL]: ['gender', 'birthdate', 'fiscalRegion'],
  [PLAN.ES_WALLET]: ['gender', 'birthdate', 'fiscalRegion'],
  [PLAN.ES_HYBRID]: ['gender', 'birthdate', 'fiscalRegion'],
};

export const getSectionsByPlan = (
  plan: TPlan = PLAN.WALLET
): Record<TPersonalInfoSection, boolean> => {
  const sections = sectionsByPlan[plan];
  if (!sections) {
    throw new Error(ERRORS.PLAN_NOT_SUPPORTED);
  }
  return convertToObj(sections);
};

export const getPersonalFieldsByPlan = (
  plan: TPlan = PLAN.WALLET
): Record<TPersonalDetailsField, boolean> => {
  const fields = personalFieldsByPlan[plan];
  if (!fields) {
    throw new Error(ERRORS.PLAN_NOT_SUPPORTED);
  }
  return convertToObj(fields);
};

const convertToObj = <T extends string>(arr: T[]) =>
  arr.reduce((acc, key) => ({ ...acc, [key]: true }), {} as Record<T, boolean>);

export function useShouldShowTaxProfile(enabled?: boolean) {
  const { savings, isInitialLoading } = useGetSavingsTotal({
    enabled: enabled ?? false,
  });

  if (isInitialLoading || !savings) {
    return false;
  }

  return true;
}
